import React, { useEffect, useState } from 'react';
import { Input, Modal, PhoneInput, TextArea, useForm } from 'linkwithus';
import './styles.scss';
import { motion } from 'framer-motion';
import config from 'config';
import { notification } from 'antd';
import { useTranslation } from 'react-i18next';
import SiteButton from 'components/SiteButton';
import FileUploader from 'components/FileUploader';
import { phoneRegex } from 'utils/regex';
import {
    AccommodationModalMode,
    IAccommodationForm,
    IAccommodationModalProps,
} from './interface';
import { contentSettingsModalMotion } from './animation';

function AccommodationModal({
    modalVisibility,
    close,
    item,
    mode = AccommodationModalMode.Edit,
    updateList,
}: IAccommodationModalProps) {
    const { t } = useTranslation();
    const [imageUrl, setImageUrl] = useState<string>();
    const [imageVisibility, setImageVisibility] = useState(false);

    const { register, form, submit, updateForm, setForm } =
        useForm<IAccommodationForm>({
            url: `/accommodations/${
                mode === AccommodationModalMode.Edit && item ? item.Id : ''
            }`,
            type: mode === AccommodationModalMode.Edit ? 'PUT' : 'POST',
            onSuccess: () => {
                notification.success({
                    message: t('general.SUCCESS'),
                    description: t(
                        `contentSettingsPage.${
                            mode === AccommodationModalMode.Edit
                                ? 'ACCOMMODATION_EDITED_WITH_SUCCESS'
                                : 'ACCOMMODATION_CREATED_WITH_SUCCESS'
                        }`,
                    ),
                });
                updateList();
                close();
            },
            onError: (data, error: any) => {
                const errorCode = Number(error.errorCode) || 0;
                notification.error({
                    message: t(`general.ERROR`),
                    description: t(`errors.${errorCode}`),
                });
                updateList();
                close();
            },
            resolveData(rawForm) {
                const data = new FormData();
                Object.keys(rawForm).forEach(key => {
                    if (key === 'photo') {
                        rawForm.photo.forEach((f: any) => {
                            data.append('photo', f as any);
                        });
                    } else
                        data.append(
                            key,
                            rawForm[key as keyof IAccommodationForm] as any,
                        );
                });
                return data;
            },
        });

    useEffect(() => {
        if (modalVisibility && mode === AccommodationModalMode.Edit && !item) {
            close();
        }
        if (modalVisibility && mode === AccommodationModalMode.Edit && item) {
            const currentDate = new Date();
            updateForm('name', item.Accommodationlanguages[0].Name);
            updateForm('location', item.Accommodationlanguages[0].Location);
            updateForm('contact', item.Contact);
            setImageUrl(
                `${config.API_URL}/accommodations/images/${item.Id}?date=${currentDate}`,
            );
            setTimeout(() => {
                setImageVisibility(true);
            }, 100);
        }
        if (!modalVisibility) {
            setForm({});
            setImageVisibility(false);
        }
        if (modalVisibility && mode === AccommodationModalMode.Create) {
            setImageUrl(undefined);
            setTimeout(() => {
                setImageVisibility(true);
            }, 100);
        }
    }, [modalVisibility]);

    return (
        <Modal
            visible={modalVisibility}
            close={close}
            className='accommodation-modal'
            width={400}
        >
            <motion.div
                {...contentSettingsModalMotion}
                className='accommodation-modal__content'
            >
                <p className='accommodation-modal__content__title'>
                    {t(
                        `contentSettingsPage.${
                            mode === AccommodationModalMode.Edit
                                ? 'EDIT_ACCOMMODATION'
                                : 'CREATE_ACCOMMODATION'
                        }`,
                    )}
                </p>
                <div className='accommodation-modal__content__body'>
                    <p className='accommodation-modal__content__body__label'>
                        {t('contentSettingsPage.ACCOMMODATION_NAME')}
                    </p>
                    <Input
                        {...register('name', {
                            required: true,
                            resolveError: () =>
                                `${t('form.NAME')} ${t('form.REQUIRED_FIELD')}`,
                        })}
                        className='accommodation-modal__content__body__field'
                    />
                    <p className='accommodation-modal__content__body__label'>
                        {t('form.CONTACT')}
                    </p>
                    <PhoneInput
                        {...register('contact', {
                            required: true,
                            regex: phoneRegex,
                            resolveError: (val, error) => {
                                if (error.type === 'regex') {
                                    return `${t('general.THE')} ${t(
                                        'form.CONTACT',
                                    )} ${t('form.FORMAT_INVALID')}`;
                                }
                                return `${t('form.CONTACT')} ${t(
                                    'form.REQUIRED_FIELD',
                                )}`;
                            },
                        })}
                        className='accommodation-modal__content__body__field'
                    />
                    <p className='accommodation-modal__content__body__label'>
                        {t('contentSettingsPage.ACCOMMODATION_LOCATION')}
                    </p>
                    <TextArea
                        {...register('location', {
                            required: true,
                            resolveError: () =>
                                `${t('form.LOCATION')} ${t('form.REQUIRED')}`,
                        })}
                        autoSize
                        className='accommodation-modal__content__body__field'
                    />
                    {imageVisibility && (
                        <FileUploader
                            {...register('photo')}
                            initial={imageUrl}
                            className='rectangular'
                        />
                    )}
                </div>
                <div className='accommodation-modal__content__footer'>
                    <SiteButton
                        text={t('general.CANCEL')}
                        type='primary'
                        className='accommodation-modal__content__footer__button grey'
                        onClick={close}
                    />
                    <SiteButton
                        text={t('form.SAVE_CHANGES')}
                        type='primary'
                        className='accommodation-modal__content__footer__button'
                        onClick={submit}
                        disabled={
                            mode === AccommodationModalMode.Create &&
                            (!form.name ||
                                !form.location ||
                                !form.photo ||
                                !form.contact ||
                                (form.contact &&
                                    !phoneRegex.test(form.contact)) ||
                                (form.photo &&
                                    Array.isArray(form.photo) &&
                                    !(form.photo.length > 0)))
                        }
                    />
                </div>
            </motion.div>
        </Modal>
    );
}

export default AccommodationModal;
